// StatementPreviewDialog.tsx
import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";

export interface PreviewDocument {
  key: string;
  filename: string;
  file_url: string | null;
}

interface StatementPreviewDialogProps {
  documents: PreviewDocument[];
  activeKey: string | null;
  onClose: () => void;
}

const StatementPreviewDialog: React.FC<StatementPreviewDialogProps> = ({
  documents,
  activeKey,
  onClose,
}) => {
  const open = activeKey !== null;

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [iframeLoading, setIframeLoading] = useState(true);

  const currentDoc = documents[currentIndex] ?? null;

  useEffect(() => {
    if (activeKey === null) return;
    const idx = documents.findIndex((d) => d.key === activeKey);
    setCurrentIndex(idx >= 0 ? idx : 0);
  }, [activeKey, documents]);

  useEffect(() => {
    setIframeLoading(true);
  }, [currentIndex]);

  // Reset the loading state whenever the dialog (re)opens so the solid backdrop
  // + "Loading…" placeholder covers the gap before the iframe paints. Without
  // this, reopening with the same index keeps a stale loaded state.
  useEffect(() => {
    if (open) setIframeLoading(true);
  }, [open, activeKey]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && currentIndex > 0) setCurrentIndex((i) => i - 1);
      else if (e.key === "ArrowRight" && currentIndex < documents.length - 1)
        setCurrentIndex((i) => i + 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, currentIndex, documents.length]);

  const showNav = documents.length > 1;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="flex h-[95vh] w-[70vw] max-w-none flex-col border-0 bg-transparent p-0 shadow-none">
        <DialogHeader className="relative flex flex-row items-center px-0 pb-3 pt-0">
          <DialogTitle className="flex-1 text-center">
            {showNav && (
              <span className="rounded-full bg-black/60 px-4 py-1 text-sm font-medium text-white">
                {currentIndex + 1} / {documents.length}
              </span>
            )}
          </DialogTitle>

          <div className="ml-auto flex items-center gap-2">
            {currentDoc?.file_url && (
              <button
                type="button"
                onClick={() => {
                  const url = currentDoc.file_url;
                  if (url) window.open(url, "_blank", "noopener");
                }}
                aria-label="Open in new tab"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            )}

            <DialogClose asChild>
              <button
                type="button"
                aria-label="Close preview"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
              >
                <X className="h-4 w-4" />
              </button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="relative flex flex-1 overflow-hidden rounded-lg bg-white dark:bg-neutral-900">
          {showNav && (
            <button
              type="button"
              aria-label="Previous document"
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex((i) => i - 1)}
              className="absolute left-2 top-1/2 z-10 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}

          {showNav && (
            <button
              type="button"
              aria-label="Next document"
              disabled={currentIndex === documents.length - 1}
              onClick={() => setCurrentIndex((i) => i + 1)}
              className="absolute right-2 top-1/2 z-10 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}

          {!currentDoc?.file_url ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              No file available.
            </div>
          ) : (
            <>
              {iframeLoading && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                  Loading…
                </div>
              )}
              <iframe
                key={currentDoc.key}
                src={currentDoc.file_url}
                title={currentDoc.filename || "Document"}
                className="h-full w-full border-0 bg-white dark:bg-neutral-900"
                onLoad={() => setIframeLoading(false)}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatementPreviewDialog;
